import type { RunRow } from "@checkone/contract/client";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { urlFor } from "../../api/client";
import { useApi, useApiMutation, useMe } from "../../api/hooks";
import { useToast } from "../../components/Toasts";
import { Empty, ErrorState, Icon, Loading, PageHeader } from "../../components/ui";
import {
  SOURCE_LABEL,
  formatDate,
  formatDateTime,
  formatDayMonth,
  formatDayShort,
  pktDate,
} from "../../lib/format";
import { ActionedCell } from "./Actioned";
import { RunView } from "./RunView";

const STATUS = {
  completed: { label: "Completed", tone: "ok" },
  stale: { label: "No new FBR file", tone: "warn" },
  failed: { label: "Failed", tone: "danger" },
} as const;

function runTitle(r: RunRow): string {
  if (r.kind === "first_check") return `First check, ${formatDate(r.finished_at)}`;
  if (r.kind === "correction") return `Correction, ${formatDate(r.finished_at)}`;
  if (r.kind === "vendor_check")
    return `Vendor check, ${formatDate(r.finished_at)} (${r.summary.vendors_checked} ${r.summary.vendors_checked === 1 ? "vendor" : "vendors"})`;
  return `Week of ${formatDate(r.finished_at)}`;
}

/** This week: the latest check in full, then every earlier one. */
export function RunsPage() {
  const runs = useApi("runs.list", { query: { limit: 20 } });
  if (runs.isPending) return <Loading />;
  if (runs.error) return <ErrorState error={runs.error} retry={() => void runs.refetch()} />;
  if (runs.data.items.length === 0)
    return (
      <>
        <PageHeader title="This week" />
        <Empty>
          No checks yet. The first check runs as soon as you <Link to="/upload">upload your vendor list</Link>
          .
        </Empty>
      </>
    );
  return (
    <div className="stack-lg">
      {/* This week is the latest whole-list check; a vendor check since covers only the vendors edited. */}
      <RunScreen
        id={(runs.data.items.find((r) => r.kind !== "vendor_check") ?? runs.data.items[0]!).id}
        runs={runs.data.items}
      />
      <AllChecks runs={runs.data.items} />
    </div>
  );
}

export function RunDetailPage() {
  const { id = "" } = useParams();
  const runs = useApi("runs.list", { query: { limit: 20 } });
  return <RunScreen id={id} runs={runs.data?.items ?? []} />;
}

/** Older and newer checks beside the title: "← 11 Oct · 25 Oct →". */
function RunNav({ id, runs }: { id: string; runs: RunRow[] }) {
  const i = runs.findIndex((r) => r.id === id);
  if (i < 0) return null;
  const older = runs[i + 1];
  const newer = runs[i - 1];
  return (
    <nav className="row" aria-label="Other checks" style={{ gap: "1.125rem", fontSize: "var(--text-base)" }}>
      {older ? (
        <Link to={`/runs/${older.id}`} aria-label={`Previous check, ${formatDate(older.finished_at)}`}>
          <Icon name="arrow-left" /> {formatDayMonth(older.finished_at)}
        </Link>
      ) : (
        <span className="muted">No earlier check</span>
      )}
      {newer && (
        <Link to={`/runs/${newer.id}`} aria-label={`Next check, ${formatDate(newer.finished_at)}`}>
          {formatDayMonth(newer.finished_at)} <Icon name="arrow-right" />
        </Link>
      )}
    </nav>
  );
}

function RunScreen({ id, runs }: { id: string; runs: RunRow[] }) {
  const run = useApi("runs.get", { params: { id } });
  const events = useApi("runs.events", { params: { id }, query: { limit: 200 } });
  const exportMut = useApiMutation("exports.create", ["exports.list"]);
  const me = useMe();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  // Viewers read the actioned trail; members and owners mark changes.
  const canMark = !!me.data && me.data.me.user.role !== "viewer";

  if (run.isPending || events.isPending) return <Loading label="Loading check…" />;
  if (run.error) return <ErrorState error={run.error} retry={() => void run.refetch()} />;
  if (events.error) return <ErrorState error={events.error} retry={() => void events.refetch()} />;

  const r = run.data.run;
  const statement = () =>
    exportMut.mutate(
      {
        body: {
          kind: "period_statement",
          params: { from: pktDate(r.started_at), to: pktDate(r.finished_at) },
        },
      },
      {
        onSuccess: () => toast.success("Statement requested. It will appear under Settings → Your data."),
        onError: (e) => toast.error(e),
      },
    );
  const unchecked = r.summary.needs_identifier;

  return (
    <div className="stack-lg">
      <RunView
        run={r}
        events={events.data.items}
        ack={{ canMark }}
        nav={<RunNav id={r.id} runs={runs} />}
        footer={
          unchecked > 0 ? (
            <>
              {unchecked} {unchecked === 1 ? "vendor" : "vendors"} could not be checked.{" "}
              <Link to="/vendors?status=needs_identifier">Add an identifier</Link> and they are checked from
              the next run.
            </>
          ) : undefined
        }
        actions={
          <>
            <a className="btn block" href={urlFor("runs.pdf", { id: r.id })} target="_blank" rel="noreferrer">
              <Icon name="file" /> Download PDF
            </a>
            <button className="btn block" onClick={statement} disabled={exportMut.isPending}>
              <Icon name="download" /> Export statement (Excel)
            </button>
            {r.run_page && (
              <button
                className="btn block"
                onClick={() => {
                  void navigator.clipboard?.writeText(r.run_page!.url).then(() => setCopied(true));
                }}
              >
                {copied ? "Link copied" : "Copy proof link"}
              </button>
            )}
            <p className="subtle" style={{ margin: 0 }}>
              A section 161 pack for one vendor is on that vendor's page.
            </p>
          </>
        }
      />
      <EarlierUnactioned runId={r.id} canMark={canMark} />
    </div>
  );
}

/**
 * Changes from earlier checks (the last five weeks) that nobody marked actioned: the same count the Monday
 * message carries. Hidden when there are none.
 */
function EarlierUnactioned({ runId, canMark }: { runId: string; canMark: boolean }) {
  const q = useApi("changes.unactioned", { query: { before_run_id: runId, limit: 200 } });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  // A marked row leaves the list, taking the focused control with it: focus moves to the row that takes its
  // place (or the one above), or to the heading when the list is empty (WCAG 2.4.3).
  const marked = useRef<{ id: string; index: number } | null>(null);
  const [clearedHere, setClearedHere] = useState(false);
  const listed = q.data?.items;
  useEffect(() => {
    const m = marked.current;
    if (!m || !listed || listed.some((e) => e.id === m.id)) return;
    marked.current = null;
    const row = listed.length ? bodyRef.current?.rows[Math.min(m.index, listed.length - 1)] : undefined;
    (row?.querySelector<HTMLElement>("button") ?? headingRef.current)?.focus();
  }, [listed]);
  if (q.isPending || q.error) return null;
  const { items, total } = q.data;
  if (total === 0) {
    // Hidden when there was nothing to mark; once the last one is marked here, say so where focus lands.
    if (!clearedHere) return null;
    return (
      <section className="panel" aria-labelledby="unactioned-h">
        <div className="panel-head">
          <h2 id="unactioned-h" ref={headingRef} tabIndex={-1}>
            Earlier weeks
          </h2>
          <span className="subtle" role="status">
            Every change from earlier weeks is marked actioned.
          </span>
        </div>
      </section>
    );
  }
  const onMarked = (id: string) => {
    marked.current = {
      id,
      index: Math.max(
        0,
        items.findIndex((e) => e.id === id),
      ),
    };
    setClearedHere(true);
  };
  return (
    <section className="panel" aria-labelledby="unactioned-h">
      <div className="panel-head">
        <h2 id="unactioned-h" ref={headingRef} tabIndex={-1}>
          Not marked actioned from earlier weeks ({total})
        </h2>
        <span className="subtle">Changes reported since {formatDate(q.data.since)}</span>
      </div>
      <div className="table-wrap responsive">
        <table className="data responsive">
          <caption className="sr-only">Status changes from earlier checks not marked actioned</caption>
          <thead>
            <tr>
              <th scope="col">Vendor</th>
              <th scope="col">List</th>
              <th scope="col">Change</th>
              <th scope="col">Reported</th>
              <th scope="col">Actioned</th>
            </tr>
          </thead>
          <tbody ref={bodyRef}>
            {items.map((e) => (
              <tr key={e.id}>
                <td data-label="">
                  <Link to={`/vendors/${e.vendor_id}`} className="vendor-name">
                    {e.vendor_name}
                  </Link>
                  <div className="ident">
                    {e.identifier.type} {e.identifier.display}
                  </div>
                </td>
                <td data-label="List" className="text-2">
                  {SOURCE_LABEL[e.source]}
                </td>
                <td data-label="Change">
                  {e.change === "added" ? "Re-entered" : "Dropped off"}
                  <div className="subtle">
                    file {formatDayShort(e.from_file.published_on)} → {formatDayShort(e.to_file.published_on)}
                  </div>
                </td>
                <td data-label="Reported">
                  <Link to={`/runs/${e.run_id}`}>{formatDate(e.detected_at)}</Link>
                </td>
                <td data-label="Actioned">
                  <ActionedCell event={e} canMark={canMark} onMarked={onMarked} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > items.length && (
        <div className="panel-foot subtle">
          Showing the oldest {items.length} of {total}.
        </div>
      )}
    </section>
  );
}

function AllChecks({ runs }: { runs: RunRow[] }) {
  return (
    <section className="panel" aria-labelledby="all-checks-h">
      <div className="panel-head">
        <h2 id="all-checks-h">All checks</h2>
        <span className="subtle">Append-only: no check can be edited after it runs</span>
      </div>
      <div className="table-wrap responsive">
        <table className="data responsive">
          <caption className="sr-only">Check runs</caption>
          <thead>
            <tr>
              <th scope="col">Check</th>
              <th scope="col">Status</th>
              <th scope="col" className="num">
                Vendors
              </th>
              <th scope="col" className="num">
                Changes
              </th>
              <th scope="col">FBR files</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <td data-label="">
                  <Link to={`/runs/${r.id}`} className="vendor-name">
                    {runTitle(r)}
                  </Link>
                  <div className="subtle">{formatDateTime(r.finished_at)}</div>
                </td>
                <td data-label="Status">
                  <span className={`pill ${STATUS[r.status].tone}`}>{STATUS[r.status].label}</span>
                </td>
                <td data-label="Vendors" className="num">
                  {r.summary.vendors_checked}
                </td>
                <td data-label="Changes" className="num">
                  {r.summary.changes}
                </td>
                <td data-label="FBR files" className="subtle">
                  {r.files
                    .map((f) => `${f.source === "ATL_IT" ? "IT" : "ST"} ${formatDayShort(f.published_on)}`)
                    .join(" · ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Signed page linked from WhatsApp and email: no login for 7 days. Same component as the app. */
export function PublicRunPage() {
  const { token = "" } = useParams();
  const page = useApi("runPages.get", { params: { token } }, { retry: false });
  if (page.isPending) return <Loading label="Loading check…" />;
  if (page.error?.code === "run_page_expired")
    return (
      <div className="auth">
        <div>
          <h1>This link has expired</h1>
          <p className="lede">
            Proof links work without signing in for 7 days. Sign in to see this check and every earlier one.
          </p>
        </div>
        <p>
          <Link className="btn primary" to="/login">
            Sign in
          </Link>
        </p>
      </div>
    );
  if (page.error) return <ErrorState error={page.error} />;
  const d = page.data;
  return (
    <div className="stack">
      {d.tenant.branding && (
        <p className="subtle" style={{ margin: 0 }}>
          Sent by {d.tenant.branding.display_name}
        </p>
      )}
      <RunView run={d.run} events={d.events} tenantName={d.tenant.name} />
      <p className="subtle">This page is available without sign-in until {formatDateTime(d.expires_at)}.</p>
    </div>
  );
}
